import React from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, Download, Minus, Plus, X } from 'lucide-react';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import {
  getInboxAttachmentPreviewMode,
  needsConvertedImagePreview,
  withInboxAttachmentInline,
  withInboxAttachmentPreview,
  type InboxAttachmentPreviewTarget,
} from '../inboxAttachmentUtils';
import { PdfDocumentViewer } from '../../../components/PdfDocumentViewer';
import { SpreadsheetViewer } from '../../../components/SpreadsheetViewer';
import { PdfPrintButton } from '../../../components/PdfPrintButton';
import { PdfMailShareButton } from '../../../components/PdfMailShareButton';
import { spreadsheetPreviewKind } from '../../../utils/spreadsheetPreview';

type Props = {
  attachment: InboxAttachmentPreviewTarget | null;
  onClose: () => void;
  onError?: (message: string) => void;
  /** false im Mail-Fenster selbst (sonst öffnet „Per E-Mail senden“ ein Mail-Fenster im Mail-Fenster). */
  allowMailShare?: boolean;
};

export function InboxAttachmentPreviewModal({ attachment, onClose, onError, allowMailShare = true }: Props) {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [zoom, setZoom] = React.useState(1);

  React.useEffect(() => {
    if (!attachment) {
      setError(null);
      setLoading(false);
      setZoom(1);
      return;
    }
    const previewMode = getInboxAttachmentPreviewMode(
      attachment.filename || 'Anhang',
      attachment.contentType ?? null,
    );
    setLoading(previewMode === 'image');
    setError(null);
    setZoom(1);
  }, [attachment]);

  React.useEffect(() => {
    if (!attachment) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [attachment, onClose]);

  if (!attachment) return null;

  const filename = attachment.filename || 'Anhang';
  const contentType = attachment.contentType ?? null;
  const previewMode = getInboxAttachmentPreviewMode(filename, contentType);
  const inlineUrl = needsConvertedImagePreview(filename, contentType)
    ? withInboxAttachmentPreview(attachment.downloadUrl)
    : withInboxAttachmentInline(attachment.downloadUrl);

  const handleLoadError = () => {
    setLoading(false);
    // Ein <img> verrät den Grund nicht; die Download-Route nennt ihn als JSON (z. B. IMAP-Server stumm).
    void fetch(inlineUrl)
      .then(async (res) => (res.ok ? null : ((await res.json().catch(() => null)) as { error?: unknown } | null)?.error))
      .catch(() => null)
      .then((reason) => {
        const message = typeof reason === 'string' && reason ? reason : 'Anhang konnte nicht geladen werden.';
        setError(message);
        onError?.(message);
      });
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[350] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="flex h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-xl bg-app-surface shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-app-border px-4 py-3">
          <h3 className="truncate pr-3 text-sm font-semibold text-app-text sm:text-base" title={filename}>
            {filename}
          </h3>
          <div className="flex shrink-0 items-center gap-1.5">
            {previewMode === 'image' && !loading && !error && (
              <>
                <button
                  type="button"
                  onClick={() => setZoom((value) => Math.max(0.5, Number((value - 0.1).toFixed(2))))}
                  className="rounded-md p-1.5 text-app-text-subtle transition hover:bg-app-hover hover:text-app-text-muted"
                  aria-label="Herauszoomen"
                  title="Herauszoomen"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setZoom(1)}
                  className="rounded-md px-2.5 py-1 text-xs font-medium text-app-text-muted transition hover:bg-app-hover"
                  aria-label="Zoom zurücksetzen"
                  title="Zoom zurücksetzen"
                >
                  {Math.round(zoom * 100)}%
                </button>
                <button
                  type="button"
                  onClick={() => setZoom((value) => Math.min(4, Number((value + 0.1).toFixed(2))))}
                  className="rounded-md p-1.5 text-app-text-subtle transition hover:bg-app-hover hover:text-app-text-muted"
                  aria-label="Hereinzoomen"
                  title="Hereinzoomen"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </>
            )}
            {previewMode === 'pdf' && allowMailShare ? (
              <PdfMailShareButton
                url={attachment.downloadUrl}
                filename={filename}
                variant="icon"
                onBeforeOpen={onClose}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-app-text-subtle transition hover:bg-app-hover hover:text-app-text-muted"
              />
            ) : null}
            {previewMode === 'pdf' ? (
              <PdfPrintButton
                url={inlineUrl}
                variant="icon"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-app-text-subtle transition hover:bg-app-hover hover:text-app-text-muted"
              />
            ) : null}
            <a
              href={attachment.downloadUrl}
              download={filename}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-app-text-subtle transition hover:bg-app-hover hover:text-app-text-muted"
              title="Herunterladen"
              aria-label="Herunterladen"
              onClick={(event) => event.stopPropagation()}
            >
              <Download className="h-4 w-4" />
            </a>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1.5 text-app-text-subtle transition hover:bg-app-hover hover:text-app-text-muted"
              aria-label="Vorschau schließen"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="relative min-h-0 flex-1 bg-app-surface-muted">
          {loading && !error && (
            <div className="absolute inset-0 z-[1] grid place-items-center bg-app-surface-muted/80">
              <InlineLoadingState label="Wird geladen…" />
            </div>
          )}
          {error ? (
            <div className="flex h-full items-start gap-3 p-6">
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                <p>{error}</p>
              </div>
            </div>
          ) : previewMode === 'image' ? (
            <div className="flex h-full w-full items-center justify-center overflow-auto bg-app-surface p-4">
              <img
                src={inlineUrl}
                alt={filename}
                className="max-h-full max-w-full object-contain"
                style={{ transform: `scale(${zoom})`, transformOrigin: 'center center' }}
                onLoad={() => setLoading(false)}
                onError={handleLoadError}
              />
            </div>
          ) : previewMode === 'pdf' ? (
            <PdfDocumentViewer
              url={inlineUrl}
              title={filename}
              openUrl={attachment.downloadUrl}
              className="h-full w-full"
            />
          ) : previewMode === 'spreadsheet' ? (
            <SpreadsheetViewer
              url={attachment.downloadUrl}
              filename={filename}
              kind={spreadsheetPreviewKind(filename, contentType)!}
              className="h-full w-full"
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
              <p className="text-sm text-app-text-muted">Für diesen Dateityp ist keine Inline-Vorschau verfügbar.</p>
              <a
                href={attachment.downloadUrl}
                download={filename}
                className="inline-flex items-center gap-2 rounded-lg bg-pipedrive-blue px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-600"
              >
                <Download className="h-4 w-4" />
                Herunterladen
              </a>
            </div>
          )}
        </div>
      </div>
    </div>,
    window.document.body,
  );
}
